import { Outcome } from '@agilelab/plugin-wb-builder-common';
import {
  WbAlert,
  WbButton,
  WbDrawerActions,
  WbDrawerContent,
} from '@agilelab/plugin-wb-platform';
import { Box, Typography } from '@material-ui/core';
import { FC, useEffect, useMemo, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { useDeploymentPreviews } from '../hooks/useDeploymentPreviews';
import { PreviewMessage } from '../types';
import { CleanUpDisclosure } from './CleanUpDisclosure';
import { NotInTargetDescriptorDisclosure } from './NotInTargetDescriptorDisclosure';
import { useDeploymentPreviewContext } from './context/useDeploymentPreviewContext';
import { DeploymentPreviewTable } from './DeploymentPreviewTable';
import { buildConflictPreviewMessage, createPreviews } from './utils';

export const BasicProvisioning: FC = () => {
  const { partialProvision, checkPreview, previews, setPreviews } =
    useDeploymentPreviewContext();
  const { fetchData } = useDeploymentPreviews();
  const [{ value: data, loading, error }, loadData] = useAsyncFn(fetchData, [
    fetchData,
  ]);
  const { isDeploymentPreviewOpen, setIsDeploymentPreviewOpen, fetchDeploys } =
    useReleaseDetailPageContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<PreviewMessage | undefined>();

  useEffect(() => {
    if (!loading) {
      setPreviews([
        ...(data?.rootComponent ? [data.rootComponent] : []),
        ...(data?.previews ?? []),
      ]);
    }
  }, [loading, data, setPreviews]);

  useEffect(() => {
    if (isDeploymentPreviewOpen) loadData();
  }, [isDeploymentPreviewOpen, loadData]);

  useEffect(() => {
    if (error)
      setMessage({
        type: 'error',
        title: 'Error',
        content: <Typography>{error.message}</Typography>,
      });
    else setMessage(undefined);
  }, [error]);

  const hasCleanUpOperations = useMemo(() => {
    return Boolean(
      data?.notInTargetDescriptorOperations.length ||
        data?.cleanUpOperations.length,
    );
  }, [data]);

  return (
    <>
      <WbDrawerContent>
        <Box display="flex" flexDirection="column" flex="1" overflow="auto">
          {message && <WbAlert message={message} />}

          {!error && (
            <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <DeploymentPreviewTable rows={previews} loading={loading} />

              {hasCleanUpOperations && (
                <Box padding="16px">
                  <Typography style={{ marginBottom: '16px', fontWeight: 500 }}>
                    Clean-up operations
                  </Typography>

                  <NotInTargetDescriptorDisclosure
                    ids={data?.notInTargetDescriptorOperations || []}
                  />
                  <CleanUpDisclosure ids={data?.cleanUpOperations || []} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </WbDrawerContent>

      <WbDrawerActions>
        <WbButton
          variant="outlined"
          color="primary"
          size="small"
          label="Cancel"
          onClick={() => {
            setIsDeploymentPreviewOpen(false);
          }}
        />

        <WbButton
          disabled={Boolean(isLoading || error)}
          loading={isLoading}
          onClick={async () => {
            const newPreviews = createPreviews(previews);

            setIsLoading(true);
            try {
              const previewResponse = await checkPreview(newPreviews);

              if (previewResponse.outcome !== Outcome.ABORTED_FOR_CONFLICTS) {
                await partialProvision(newPreviews);

                setIsDeploymentPreviewOpen(false);
                fetchDeploys();
              } else {
                const conflictMessage = buildConflictPreviewMessage(
                  previews,
                  previewResponse.conflictingOperations ?? [],
                );
                setMessage({
                  type: 'error',
                  title: 'Error',
                  content: <Typography>{conflictMessage}</Typography>,
                });
              }
            } catch (e) {
              const errorList: string[] = [
                ...(e.moreInfo?.problems ?? []),
                ...(e.moreInfo?.solutions ?? []),
              ];
              setMessage({
                type: 'error',
                title: e.message,
                content: (
                  <>
                    {errorList.map((msg, i) => {
                      return <Typography key={i}>{msg}</Typography>;
                    })}
                  </>
                ),
              });
            } finally {
              setIsLoading(false);
            }
          }}
          variant="contained"
          size="small"
          color="primary"
          label="Confirm"
        />
      </WbDrawerActions>
    </>
  );
};
